"use client";

import { Button } from "@shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@shared/components/ui/dropdown-menu";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@shared/components/ui/sidebar";
import { Skeleton } from "@shared/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shared/components/ui/tooltip";
import { IconBuilding, IconChevronDown, IconCoin } from "@tabler/icons-react";
import { AnimatePresence, motion } from "framer-motion";
import { gql } from "graphql-request";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { AddCreditsDialog } from "@/app/(app)/manage/billing/billing.overview";
import { useOptionalWelcomeCreditReveal } from "@/components/welcome-credit-reveal";
import { useAuth } from "@/lib/auth";
import { useBillingOverview } from "@/lib/billing/useBillingOverview";
import { useCreditTopUp } from "@/lib/billing/useCreditTopUp";
import { useGraphqlMutation } from "@/lib/graphql/useGraphql";
import { appSidebarNavFooter } from "@/lib/navigation/app-sidebar-nav";
import { orgRoleCanManageBilling } from "@/lib/org/org-constants";
import { toastUserError } from "@/lib/toast-user-error";

const ADD_CREDITS_REVEAL_DELAY_MS = 500;

const setLastUsedOrgDocument = gql`
  mutation SetLastUsedOrg($orgId: ID!) {
    setLastUsedOrg(org_id: $orgId) {
      id
      kind
      name
    }
  }
`;

const slideInFromRight = {
  initial: { opacity: 0, x: 12 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 12 },
  transition: { duration: 0.2, ease: [0, 0, 0.2, 1] },
} as const;

const formatUsdFromMicro = (micro: string | number | null | undefined) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Number(micro ?? 0) / 1_000_000);

const NavCreditsItem = () => {
  const { state, isMobile } = useSidebar();
  const collapsed = state === "collapsed" && !isMobile;
  const { orgRole } = useAuth();
  const canManageBilling = orgRoleCanManageBilling(orgRole);
  const { data, isPending, isError } = useBillingOverview();
  const topUp = useCreditTopUp();
  const overview = data?.billing_overview;
  const welcomeReveal = useOptionalWelcomeCreditReveal();
  const displayMicro = welcomeReveal
    ? welcomeReveal.displayBalanceMicro(overview?.credit_balance_micro)
    : Number(overview?.credit_balance_micro ?? 0);
  const balanceLabel = isError || (!isPending && overview == null) ? "—" : formatUsdFromMicro(displayMicro);
  const [revealAdd, setRevealAdd] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const revealTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showAdd = canManageBilling && (revealAdd || dialogOpen);

  useEffect(() => {
    return () => {
      if (revealTimeoutRef.current != null) {
        clearTimeout(revealTimeoutRef.current);
      }
    };
  }, []);

  const handlePointerEnter = () => {
    if (!canManageBilling) {
      return;
    }
    if (revealTimeoutRef.current != null) {
      clearTimeout(revealTimeoutRef.current);
    }
    revealTimeoutRef.current = setTimeout(() => {
      setRevealAdd(true);
      revealTimeoutRef.current = null;
    }, ADD_CREDITS_REVEAL_DELAY_MS);
  };

  const handlePointerLeave = () => {
    if (revealTimeoutRef.current != null) {
      clearTimeout(revealTimeoutRef.current);
      revealTimeoutRef.current = null;
    }
    setRevealAdd(false);
  };

  if (collapsed) {
    return (
      <SidebarMenuItem>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex size-8 items-center justify-center [&>span]:hidden">
              <IconCoin className="size-4 shrink-0" aria-hidden />
              <span>Credits {balanceLabel}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="right" align="center">
            Credits {balanceLabel}
          </TooltipContent>
        </Tooltip>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      {/* Non-interactive row — hover only reveals Add credits; not a control itself. */}
      {/* biome-ignore lint/a11y/useSemanticElements: hover-affordance row, not a form fieldset */}
      <div
        role="group"
        aria-label="Credits"
        className="flex h-8 w-full items-center gap-2 rounded-[2px] p-2 text-sm"
        onMouseEnter={handlePointerEnter}
        onMouseLeave={handlePointerLeave}
        onFocusCapture={() => {
          if (!canManageBilling) {
            return;
          }
          setRevealAdd(true);
        }}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
            setRevealAdd(false);
          }
        }}
      >
        <IconCoin className="size-4 shrink-0" aria-hidden />
        <span className="truncate">Credits</span>
        <div className="relative ml-auto flex h-7 min-w-0 shrink-0 items-center justify-end overflow-visible">
          {isPending ? (
            <Skeleton className="h-4 w-12" />
          ) : (
            <>
              <span className={`text-muted-foreground ${showAdd ? "invisible" : ""}`}>{balanceLabel}</span>
              <AnimatePresence>
                {showAdd && overview != null ? (
                  <motion.div
                    key="add-credits"
                    className="absolute inset-y-0 right-0 flex items-center"
                    initial={slideInFromRight.initial}
                    animate={slideInFromRight.animate}
                    exit={slideInFromRight.exit}
                    transition={slideInFromRight.transition}
                  >
                    <AddCreditsDialog
                      topUp={topUp}
                      paymentMethods={overview.payment_methods}
                      invoices={overview.invoices}
                      monthlySpendLimitMicro={overview.monthly_spend_limit_micro}
                      currentSettings={overview.auto_recharge}
                      onOpenChange={setDialogOpen}
                      trigger={
                        <Button size="sm" className="h-7 px-2 text-xs">
                          Add credits
                        </Button>
                      }
                    />
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </>
          )}
        </div>
      </div>
    </SidebarMenuItem>
  );
};

const NavOrgItem = () => {
  const { state, isMobile } = useSidebar();
  const collapsed = state === "collapsed" && !isMobile;
  const { status, orgId, orgName, orgs } = useAuth();
  const setLastUsedOrg = useGraphqlMutation<
    { setLastUsedOrg: { id: string; kind: string; name: string } },
    { orgId: string }
  >({ document: setLastUsedOrgDocument });

  if (status !== "authenticated" || !orgName) {
    return null;
  }

  const canSwitch = orgs.length > 1;

  const handleSwitchOrg = async (nextOrgId: string) => {
    if (!nextOrgId || nextOrgId === orgId || setLastUsedOrg.isPending) {
      return;
    }
    try {
      await setLastUsedOrg.mutateAsync({ orgId: nextOrgId });
      window.location.assign(window.location.pathname + window.location.search);
    } catch (error) {
      toastUserError(error, "Failed to switch organization");
    }
  };

  if (collapsed && !canSwitch) {
    return (
      <SidebarMenuItem>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex size-8 items-center justify-center [&>span]:hidden">
              <IconBuilding className="size-4 shrink-0" aria-hidden />
              <span>{orgName}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="right" align="center">
            {orgName}
          </TooltipContent>
        </Tooltip>
      </SidebarMenuItem>
    );
  }

  if (!canSwitch) {
    return (
      <SidebarMenuItem>
        {/* biome-ignore lint/a11y/useSemanticElements: display-only org label, not a form fieldset */}
        <div
          role="group"
          className="flex h-8 w-full items-center gap-2 rounded-control p-2 text-sm"
          aria-label={`Organization ${orgName}`}
        >
          <IconBuilding className="size-4 shrink-0" aria-hidden />
          <span className="truncate">{orgName}</span>
        </div>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton tooltip={orgName} className="data-[state=open]:bg-sidebar-accent">
            <IconBuilding className="size-4" aria-hidden />
            <span className="truncate">{orgName}</span>
            {!collapsed ? <IconChevronDown className="ml-auto size-4 opacity-60" aria-hidden /> : null}
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="min-w-56 rounded-lg" align="start" sideOffset={4}>
          <DropdownMenuRadioGroup
            value={orgId ?? undefined}
            onValueChange={(value) => void handleSwitchOrg(String(value))}
          >
            {orgs.map((org) => (
              <DropdownMenuRadioItem key={org.id} value={org.id}>
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate">{org.name}</span>
                  <span className="text-muted-foreground truncate text-xs capitalize">{org.kind}</span>
                </span>
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
};

/**
 * Utility links above the account menu: Docs + Credits + Organization.
 */
export const NavSidebarUtility = () => {
  const pathname = usePathname();
  const { docsSettings } = useAuth();
  const showDocs = docsSettings?.globally_enabled !== false;

  return (
    // Match main nav group padding (0.5rem 0.75rem) and menu gap (0.25rem / gap-1).
    <SidebarMenu className="px-3 py-2">
      {showDocs
        ? appSidebarNavFooter.map((item) => {
            const Icon = item.icon;
            const itemBase = item.url.replace(/\/$/, "");
            const isActive = pathname === item.url || pathname.startsWith(`${itemBase}/`);
            return (
              <SidebarMenuItem key={item.url}>
                <SidebarMenuButton asChild isActive={isActive} tooltip={item.title}>
                  <Link href={item.url}>
                    <Icon className="size-4" aria-hidden />
                    <span>{item.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })
        : null}
      <NavCreditsItem />
      <NavOrgItem />
    </SidebarMenu>
  );
};
